import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../config/api';
import { useToast } from '../context/ToastContext';
import headerStyles from '../styles/modules/Header.module.css';
import pageStyles from '../styles/modules/PageLayout.module.css';
import styles from '../styles/modules/AddAvailability.module.css';
import backgroundImage from '../images/background.jpg';

const AddAvailability: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showToast } = useToast();
    const minDate = `${new Date().getFullYear()}-04-01`;
    const [formData, setFormData] = useState({
        date: '',
        startTime: '',
        location: '',
        payForTransport: false,
        notes: ''
    });

    const cities = [
        'Belgrade',
        'Novi Sad',
        'Niš',
        'Kragujevac',
        'Subotica',
        'Zrenjanin',
        'Pančevo',
        'Čačak',
        'Kraljevo',
        'Smederevo'
    ];

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        const token = localStorage.getItem('authToken');
        if (!token) {
            showToast({ message: t('common.loginRequired'), type: 'error' });
            navigate('/');
            return;
        }

        try {
            const availabilityData = {
                date: formData.date, // YYYY-MM-DD format
                startTime: formData.startTime, // HH:MM format
                place: formData.location,
                needsTransport: formData.payForTransport,
                notes: formData.notes
            };

            console.log('Sending availability data:', availabilityData);

            const response = await fetch(getApiUrl('availability'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(availabilityData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('API Error:', errorData);
                throw new Error(errorData.message || 'Failed to add availability');
            }

            const result = await response.json();
            console.log('Availability added successfully:', result);
            
            // Clear form
            setFormData({
                date: '',
                startTime: '',
                location: '',
                payForTransport: false,
                notes: ''
            });

            // Navigate to my availability page to view all availabilities
            showToast({ message: t('addAvailability.addSuccess'), type: 'success' });
            navigate('/my-availability');
        } catch (error) {
            console.error('Error adding availability:', error);
            showToast({
                message: error instanceof Error ? error.message : t('addAvailability.addFailed'),
                type: 'error'
            });
        }
    };

    const handleBackClick = () => {
        navigate('/dashboard');
    };

    return (
        <div className={styles.addAvailabilityPage}>
            <header className={headerStyles.header}>
                <button className={`${headerStyles.backButton} ${headerStyles.backButtonAbsolute}`} onClick={handleBackClick}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                </button>
                <h1 className={styles.headerTitle}>{t('addAvailability.title')}</h1>
                <div></div>
            </header>

            <div className={pageStyles.container} style={{ backgroundImage: `url(${backgroundImage})` }}>
                <div className={styles.contentWrapper}>
                    <form className={styles.availabilityForm} onSubmit={handleSubmit}>
                        <div className={styles.formGroup}>
                            <label htmlFor="date" className={styles.formLabel}>{t('addAvailability.selectDate')}</label>
                            <input
                                type="date"
                                id="date"
                                name="date"
                                value={formData.date}
                                onChange={handleInputChange}
                                className={styles.formInput}
                                min={minDate}
                                required
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="startTime" className={styles.formLabel}>{t('addAvailability.startTime')}</label>
                            <input
                                type="time"
                                id="startTime"
                                name="startTime"
                                value={formData.startTime}
                                onChange={handleInputChange}
                                className={styles.formInput}
                                required
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="location" className={styles.formLabel}>{t('addAvailability.place')}</label>
                            <div className={styles.locationInputWrapper}>
                                <select
                                    id="location"
                                    name="location"
                                    value={formData.location}
                                    onChange={handleInputChange}
                                    className={`${styles.formInput} ${styles.formSelect}`}
                                    required
                                >
                                    <option value="">{t('addAvailability.enterLocation')}</option>
                                    {cities.map(city => (
                                        <option key={city} value={city}>{city}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>{t('addAvailability.needTransport')}</label>
                            <div className={styles.checkboxWrapper}>
                                <input
                                    type="checkbox"
                                    name="payForTransport"
                                    checked={formData.payForTransport}
                                    onChange={handleInputChange}
                                    className={styles.checkboxInput}
                                />
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="notes" className={styles.formLabel}>{t('addAvailability.additionalNotes')}</label>
                            <textarea
                                id="notes"
                                name="notes"
                                value={formData.notes}
                                onChange={handleInputChange}
                                className={`${styles.formInput} ${styles.formTextarea}`}
                                rows={3}
                                placeholder={t('addAvailability.addNotes')}
                                style={{ resize: 'vertical', minHeight: '80px' }}
                            />
                        </div>

                        <button type="submit" className={styles.submitButton}>
                            {t('addAvailability.submit')}
                        </button>
                    </form>
                </div>
            </div>

        </div>
    );
};

export default AddAvailability;
